import { Component, Inject } from '@angular/core';
import {
  MAT_DIALOG_DATA,
  MatDialog,
  MatDialogRef,
} from '@angular/material/dialog';
import { EnterEmailModalComponent } from '../enter-email-modal/enter-email-modal.component';
import { OrderApiService } from 'src/app/services/api/order-api.service';
import { MessageService } from 'src/app/services/message.service';
import { Router } from '@angular/router';

@Component({
  selector: 'app-repair-fail-modal',
  templateUrl: './repair-fail-modal.component.html',
  styleUrls: ['./repair-fail-modal.component.scss'],
})
export class RepairFailModalComponent {
  index = 1;

  // 个人会员流程步骤: 0=选择方案, 1=概览, 2=填写账号, 3=确认提交, 4=处理中, 5=成功
  step = 0;

  // 表单字段
  email = '';
  password = '';
  backupCode1 = '';
  backupCode2 = '';
  showPassword = false;
  accountConfirm = false;
  submitConfirm = false;

  loading = false;

  constructor(
    protected dialog: MatDialog,
    @Inject(MAT_DIALOG_DATA) public data: any,
    private dialogRef: MatDialogRef<RepairFailModalComponent>,
    private api: OrderApiService,
    private message: MessageService,
    private router: Router,
  ) {}

  changeIndex(i: number) {
    this.index = i;
    this.step = 0;
  }

  /** 原订单剩余天数 */
  get remainingDays(): number {
    return this.data?.remaining_days;
  }

  /** 转换后的个人会员月数（按整月向上计算） */
  get convertedMonths(): number {
    return Math.max(1, Math.ceil(this.remainingDays / 30));
  }

  /** 剩余天数文案 */
  get remainingDaysText(): string {
    return `${this.remainingDays} 天`;
  }

  /** 转换月数文案 */
  get convertedMonthsText(): string {
    return `${this.convertedMonths} 个月`;
  }

  /** Google 账号格式校验 */
  get emailValid(): boolean {
    const v = this.email.trim();
    return v.length > 0 && v.includes('@');
  }

  /** 备用安全码格式校验（8位纯数字） */
  get backupCode1Valid(): boolean {
    return /^\d{8}$/.test(this.backupCode1.trim());
  }

  get backupCode2Valid(): boolean {
    return /^\d{8}$/.test(this.backupCode2.trim());
  }

  /** 账号表单是否可提交 */
  get accountFormValid(): boolean {
    return (
      this.emailValid &&
      this.password.length > 0 &&
      this.backupCode1Valid &&
      this.backupCode2Valid &&
      this.accountConfirm
    );
  }

  // ========== 方案选择 ==========

  /** 确认选择方案 */
  confirmPlan() {
    if (this.index === 1) {
      // 使用新邮箱加入
      this.enterEmail();
    } else if (this.index === 2) {
      // 开通个人会员
      this.step = 1;
    }
  }

  // ========== 步骤导航 ==========

  goToStep(s: number) {
    this.step = s;
  }

  goBack() {
    if (this.step > 1) {
      this.step--;
    }
  }

  // ========== 个人会员流程 ==========

  /** 从概览进入填写账号 */
  goToAccount() {
    this.step = 2;
  }

  /** 从账号表单进入核对提交 */
  goToReview() {
    if (!this.accountFormValid) {
      return;
    }
    this.submitConfirm = false;
    this.step = 3;
  }

  /** 提交处理 */
  async submitIndividualPlan() {
    if (!this.submitConfirm) {
      return;
    }
    this.step = 4;

    try {
      this.loading = true;
      const r = await this.api.youtubeToCharge(
        this.data.id,
        this.email.trim(),
        this.password,
        this.backupCode1.trim(),
        this.backupCode2.trim(),
        '',
      );
      if (r.status === 0) {
        setTimeout(() => {
          this.clearSensitiveFields();
          this.step = 5;
        }, 1100);
      } else {
        this.message.warn('提交失败，请重试');
        this.step = 2;
      }
    } catch (error) {
      this.message.warn('提交失败，请重试');
      this.step = 2;
    } finally {
      this.loading = false;
    }
  }

  /** 返回我的车票 */
  backToTickets() {
    this.dialogRef.close();
    this.router.navigateByUrl('/', { skipLocationChange: true }).then(() => {
      this.router.navigate(['/user/profile/seats']);
    });
  }

  // ========== 辅助方法 ==========

  togglePassword() {
    this.showPassword = !this.showPassword;
  }

  openBackupGuide() {
    window.open(
      'https://support.google.com/accounts/answer/1187538?co=GENIE.Platform%3DDesktop&hl=zh-Hans',
      '_blank',
      'noopener,noreferrer',
    );
  }

  private clearSensitiveFields() {
    this.password = '';
    this.backupCode1 = '';
    this.backupCode2 = '';
  }

  /** 获取表单错误信息 */
  getEmailError(): string {
    if (!this.email) return '';
    return this.emailValid ? '' : '账号格式必须包含 @';
  }

  getPasswordError(): string {
    if (!this.password) return '';
    return this.password.length > 0 ? '' : '请输入账号密码';
  }

  getBackupCode1Error(): string {
    if (!this.backupCode1) return '';
    return this.backupCode1Valid ? '' : '备用安全码必须是 8 位纯数字';
  }

  getBackupCode2Error(): string {
    if (!this.backupCode2) return '';
    return this.backupCode2Valid ? '' : '备用安全码必须是 8 位纯数字';
  }

  enterEmail() {
    this.dialogRef.close();
    this.dialog.open(EnterEmailModalComponent, {
      width: '570px',
      data: Object.assign({}, this.data),
      panelClass: 'app-dialog',
    });
  }
}
